import React from 'react';
import { post, patch } from 'helpers/api';

export const GATE_DRAG_OPTIONS = {
  COLLECT_INFORMATION: 'collect_information',
  SIGN_CA: 'sign_ca',
  VERIFICATION: 'verification',
  WAITLIST: 'waitlist'
};

export const GATE_FIRST_OPTION = 'public_access';
export const GATE_LAST_OPTION = 'high';
const REQUIRED_GATES = [GATE_FIRST_OPTION, 'low'];
export const GATE_SET_OPTIONS = [...REQUIRED_GATES, 'medium', GATE_LAST_OPTION];

export const GATE_ROLES = [{ type: 'broker', label: 'Broker' }, { type: 'owner', label: 'Principal/Investor' }];
export const GATE_TYPES = { NOT_IN_PROGRESS: 'not_in_progress', IN_PROGRESS: 'in_progress' };

const collectInformationHelpText = `Require viewers to enter in critical information to know who is accessing
                                    documents`;
const verificationHelpText = `Requires viewers to create a connect account`;
const signCaHelpText = `Requires viewers to electronically execute a confidentiality agreement in order to view locked
                        documents`;
const waitlistHelpText = `Requires viewers to  request access to your locked documents. Approval is required.`;

export const GATE_HELP_TEXT = {
  [GATE_DRAG_OPTIONS.COLLECT_INFORMATION]: collectInformationHelpText,
  [GATE_DRAG_OPTIONS.VERIFICATION]: verificationHelpText,
  [GATE_DRAG_OPTIONS.SIGN_CA]: signCaHelpText,
  [GATE_DRAG_OPTIONS.WAITLIST]: waitlistHelpText,
};

export const gateTitleToHeading = (title) => {
  switch (title) {
  case GATE_DRAG_OPTIONS.SIGN_CA:
    return 'Sign CA';
  case GATE_FIRST_OPTION:
    return 'Public';
  default:
    return <span className="text-capitalize">{title.replace('_', ' ')}</span>;
  }
};

export function handleSameTypeLeftPlacement(gates, _hoverType, dragType, hoverIndex, dragIndex) {
  const gatesToChange = gates[dragType];

  return {
    ...gates,
    [dragType]: [
      ...gatesToChange.slice(0, hoverIndex),
      gatesToChange[dragIndex],
      ...gatesToChange.slice(hoverIndex, dragIndex),
      ...gatesToChange.slice(dragIndex + 1),
    ]
  };
}

export function handleSameTypeRightPlacement(gates, _hoverType, dragType, hoverIndex, dragIndex) {
  const gatesToChange = gates[dragType];

  return {
    ...gates,
    [dragType]: [
      ...gatesToChange.slice(0, dragIndex),
      ...gatesToChange.slice(dragIndex + 1, hoverIndex + 1),
      gatesToChange[dragIndex],
      ...gatesToChange.slice(hoverIndex + 1),
    ]
  };
}

export function handleDifferentTypeLeftPlacement(gates, hoverType, dragType, hoverIndex, dragIndex) {
  const gatesToAddTo = gates[hoverType];
  const gatesToRemoveFrom = gates[dragType];

  return {
    [dragType]: gatesToRemoveFrom.filter((gate, i) => i !== dragIndex),
    [hoverType]: [
      ...gatesToAddTo.slice(0, hoverIndex),
      gatesToRemoveFrom[dragIndex],
      ...gatesToAddTo.slice(hoverIndex)
    ]
  };
}

export function handleDifferentTypeRightPlacement(prevGates, hoverType, dragType, hoverIndex, dragIndex) {
  const gatesToAddTo = prevGates[hoverType];
  const gatesToRemoveFrom = prevGates[dragType];

  return {
    [dragType]: gatesToRemoveFrom.filter((gate, i) => i !== dragIndex),
    [hoverType]: [
      ...gatesToAddTo.slice(0, hoverIndex + 1),
      gatesToRemoveFrom[dragIndex],
      ...gatesToAddTo.slice(hoverIndex + 1)
    ]
  };
}

export function handleEmptyDrop(gates, index) {
  const gatesToRemoveFrom = gates[GATE_TYPES.IN_PROGRESS];
  const gate = gatesToRemoveFrom[index];

  return {
    [GATE_TYPES.NOT_IN_PROGRESS]: [gate],
    [GATE_TYPES.IN_PROGRESS]: gatesToRemoveFrom.filter((_gate, i) => index !== i)
  };
}

const DEPENDENT_GATES = [GATE_DRAG_OPTIONS.SIGN_CA, GATE_DRAG_OPTIONS.WAITLIST];
export const COLLECT_INFO_AND_REGISTRATION = [GATE_DRAG_OPTIONS.COLLECT_INFORMATION, GATE_DRAG_OPTIONS.VERIFICATION];

function isDependentValid(inProgressGates, gateContainer, gateType) {
  if (gateContainer === GATE_TYPES.NOT_IN_PROGRESS) return true;

  const gateIndex = inProgressGates.findIndex(gate => gate.gateType === gateType);

  const requiredBeforeGateIndex = inProgressGates.findIndex((gate, index) => {
    return GATE_SET_OPTIONS.includes(gate.gateType) && index > gateIndex;
  });

  return inProgressGates.some((gate, index) => {
    return index < requiredBeforeGateIndex && COLLECT_INFO_AND_REGISTRATION.includes(gate.gateType);
  });
}

export function parseInvalidGateTypes(gates) {
  return Object.keys(gates).reduce((accumulator, gateKey) => {
    gates[gateKey].forEach(({ gateType }) => {
      if (DEPENDENT_GATES.includes(gateType)) {
        if (!isDependentValid(gates[GATE_TYPES.IN_PROGRESS], gateKey, gateType)) accumulator.push(gateType);
      }
    });

    return accumulator;
  }, []);
}

export function getHoverPosition(ref, monitor) {
  const hoverBoundingRect = ref.current.getBoundingClientRect();
  const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
  const clientOffset = monitor.getClientOffset();
  const hoverClientX = clientOffset.x - hoverBoundingRect.left;
  const onLeftHalf = hoverClientX < hoverMiddleX;

  return onLeftHalf ? 'left' : 'right';
}

export function parseGateMovedVariables(dragType, hoverType, leftOrRight, hoverIndex, dragIndex) {
  let type, index, handleGatesMoveFunction;
  if (dragType !== hoverType) {
    if (leftOrRight === 'left') {
      handleGatesMoveFunction = handleDifferentTypeLeftPlacement;
      index = hoverIndex;
    } else if (leftOrRight === 'right') {
      handleGatesMoveFunction = handleDifferentTypeRightPlacement;
      index = hoverIndex + 1;
    }
    type = hoverType;
  } else {
    if (dragIndex > hoverIndex) {
      handleGatesMoveFunction = handleSameTypeLeftPlacement;
    } else if (dragIndex < hoverIndex) {
      handleGatesMoveFunction = handleSameTypeRightPlacement;
    }
    index = hoverIndex;
  }

  return { type, index, handleGatesMoveFunction };
}

export function vaultRoleKey(vaultId, role) {
  return `${vaultId}-${role}`;
}

export function setupInitialCaSettings(vaults) {
  return vaults.reduce((accumulator, vault) => {
    accumulator[vaultRoleKey(vault.id, 'broker')] = vault.brokerCaData;
    accumulator[vaultRoleKey(vault.id, 'owner')] = vault.ownerCaData;
    return accumulator;
  }, {});
}

export function setupInitialGates(vaults) {
  return vaults.reduce((accumulator, vault) => {
    GATE_ROLES.forEach((role) => {
      const initialGatesForRole = vault.gates.filter(gate => gate.role === role.type).filter((gate) => {
        return gate.gateType !== 'registration';
      });

      const inProgress = GATE_SET_OPTIONS.reduce((gates, gateType) => {
        const gatesBeforeCurrent = initialGatesForRole.filter(gate => gate.requiredBefore === gateType)
          .sort((gate1, gate2) => gate1.order - gate2.order);
        if (REQUIRED_GATES.includes(gateType) || vault.startingAccessLevels.includes(gateType)) {
          gates.push(...gatesBeforeCurrent);
          gates.push(gateFromOption(gateType));
        }

        return gates;
      }, []);

      const inProgressTypes = inProgress.map(gate => gate.gateType);
      const notInProgress = Object.values(GATE_DRAG_OPTIONS).filter(gateType => !inProgressTypes.includes(gateType));

      accumulator[vaultRoleKey(vault.id, role.type)] = {
        [GATE_TYPES.NOT_IN_PROGRESS]: notInProgress.map(gateFromOption),
        [GATE_TYPES.IN_PROGRESS]: inProgress
      };
    });

    return accumulator;
  }, {});
}

const gateFromOption = gateType => ({ gateType });

export function handleNewAccessLevels(gates, accessLevels, vaultId) {
  const gateKeysToPotentiallyModify = GATE_ROLES.map(gateRole => `${vaultId}-${gateRole.type}`);

  return Object.keys(gates).reduce((accumulator, gateKey) => {
    const gatesForKey = gates[gateKey];
    if (gateKeysToPotentiallyModify.includes(gateKey)) {
      const inProgressGates = modifyGatesBasedOnDocs(gatesForKey[GATE_TYPES.IN_PROGRESS], accessLevels);

      // add back in any drag options into not in progress that were removed
      const inProgressGateTypes = inProgressGates.map(gate => gate.gateType);
      const notInProgressGateTypes = gatesForKey[GATE_TYPES.NOT_IN_PROGRESS].map(gate => gate.gateType);
      const gatesToAdd = Object.values(GATE_DRAG_OPTIONS).filter((gateType) => {
        return !inProgressGateTypes.includes(gateType) && !notInProgressGateTypes.includes(gateType);
      });
      const notInProgressGates = [
        ...gatesForKey[GATE_TYPES.NOT_IN_PROGRESS],
        ...gatesToAdd.map(gateFromOption)
      ];

      accumulator[gateKey] = {
        [GATE_TYPES.NOT_IN_PROGRESS]: notInProgressGates,
        [GATE_TYPES.IN_PROGRESS]: inProgressGates
      };
    } else {
      accumulator[gateKey] = gatesForKey;
    }

    return accumulator;
  }, {});
}

function modifyGatesBasedOnDocs(inProgressGates, accessLevels) {
  const requiredGates = [...accessLevels, ...REQUIRED_GATES];

  // remove access levels if we dont have the access level for them
  const updatedGatesForGateKey = inProgressGates.filter((gate, index) => {
    if (requiredGates.includes(gate.gateType)) return true;

    if (!GATE_SET_OPTIONS.includes(gate.gateType)) {
      const requiredBefore = inProgressGates.find((findGate, findIndex) => {
        return GATE_SET_OPTIONS.includes(findGate.gateType) && index < findIndex;
      });
      return requiredGates.includes(requiredBefore.gateType);
    }
  });

  // Add access level if its not currently in the ones we have for in progress
  const gateTypesInProgress = updatedGatesForGateKey.map(gate => gate.gateType);
  accessLevels.filter(accessLevel => !gateTypesInProgress.includes(accessLevel)).forEach((accessLevel) => {
    if (accessLevel === 'medium') {
      const lowAccessIndex = updatedGatesForGateKey.findIndex(gate => gate.gateType === 'low');
      updatedGatesForGateKey.splice(lowAccessIndex + 1, 0, gateFromOption(accessLevel));
    } else if (accessLevel === 'high') {
      updatedGatesForGateKey.push(gateFromOption(accessLevel));
    }
  });

  return updatedGatesForGateKey;
}

export function createVaultPromises(vaults, gates, caSettings, bulkModifyPath) {
  const gatesArray = vaults.reduce((accumulator, vault) => {
    GATE_ROLES.forEach((role) => {
      gates[vaultRoleKey(vault.id, role.type)][GATE_TYPES.IN_PROGRESS].forEach((gate) => {
        accumulator.push({
          vault_id: vault.id,
          gate_role: role.type,
          id: gate.id,
          gate_type: gate.gateType,
        });
      });
    });

    return accumulator;
  }, []);

  const vaultPromises = vaults.reduce((accumulator, vault) => {
    const validRoles = GATE_ROLES.filter((role) => {
      const inProgressGatesForRole = gates[vaultRoleKey(vault.id, role.type)][GATE_TYPES.IN_PROGRESS];
      return inProgressGatesForRole.some(gate => gate.gateType === GATE_DRAG_OPTIONS.SIGN_CA);
    });

    if (validRoles.length === 0) return accumulator;

    const data = { full_messages: true };
    validRoles.forEach((role) => {
      const caSetting = caSettings[vaultRoleKey(vault.id, role.type)];
      const attributeKey = [`${role.type}_ca_attributes`];
      data[attributeKey] = {
        ca_type: caSetting.caType,
        text: caSetting.text
      };

      if (caSetting.hasOwnProperty('uploadId')) data[attributeKey].upload_id = caSetting.uploadId;
      if (caSetting.hasOwnProperty('wordUploadId')) data[attributeKey].word_upload_id = caSetting.wordUploadId;
      if (caSetting.hasOwnProperty('digitalSignUploadId')) {
        data[attributeKey].digital_sign_upload_id = caSetting.digitalSignUploadId;
      }
    });

    accumulator.push(new Promise((resolve, reject) => {
      patch(`/vaults/${vault.id}`, data).then(resolve).catch(reject);
    }));

    return accumulator;
  }, []);

  const vaultGatesPromise = new Promise((resolve, reject) => {
    post(bulkModifyPath, { vault_gates: gatesArray }).then(resolve).catch(reject);
  });

  return [...vaultPromises, vaultGatesPromise];
}

export function invalidDragsToRemove(title) {
  return COLLECT_INFO_AND_REGISTRATION.includes(title) ? Object.values(GATE_DRAG_OPTIONS) : [title];
}
